import { BaseProvider, type ImagePlanPrompts, type TextGenerationOptions, type EditOptions, type RoleplayOptions } from './provider-interface';
import type { ValidationResult, APIKeyConfig } from '@/types/api-key';
import type { CharacterProfile, ChatExportFields, WorldDocument } from '@shared/schema';
import type { ProviderResponse } from '@/types/usage';
import { parseOpenAIUsage } from './usage-helpers';
import { getFirebaseAuth } from '@/config/firebase';
import { buildAuthHeaders } from '@/utils/auth-headers';
import { parseJsonLike } from './json-utils';

/**
 * Robust parser for image-plan prompts when the model returns non-JSON or chatty text.
 * Falls back to extracting key/value lines when JSON is missing.
 */
function parseImagePlanPromptsRobust(text: string): ImagePlanPrompts {
  const content = String(text ?? '').trim();
  if (!content) {
    throw new Error('Empty response from free provider');
  }
  // First try tolerant JSON parsing
  try {
    return parseJsonLike<ImagePlanPrompts>(content);
  } catch {}

  // Heuristic fallback: look for key: value lines
  const result: Partial<ImagePlanPrompts> = {};
  const lines = content.split(/\r?\n/);
  const tryExtract = (key: 'naturalPrompt' | 'stableDiffusionPrompt' | 'negativePrompt') => {
    for (const rawLine of lines) {
      const line = String(rawLine).trim();
      const re = new RegExp(`(^|[\\n\\-*•\\s])"?${key}"?\\s*[:=]\\s*(.+)$`, 'i');
      const m = line.match(re);
      if (m && m[2]) {
        let val = String(m[2]).trim();
        // Strip trailing commas and surrounding quotes/backticks
        val = val.replace(/[`,]$/, '').replace(/^"|^'|^`/, '').replace(/"$|'$|`$/, '').trim();
        if (val) {
          (result as any)[key] = val;
          return;
        }
      }
    }
  };
  tryExtract('naturalPrompt');
  tryExtract('stableDiffusionPrompt');
  tryExtract('negativePrompt');

  // Minimal viability: mirror values if only one present
  if (result.naturalPrompt && !result.stableDiffusionPrompt) {
    result.stableDiffusionPrompt = result.naturalPrompt;
  }
  if (!result.naturalPrompt && result.stableDiffusionPrompt) {
    result.naturalPrompt = result.stableDiffusionPrompt;
  }

  if (result.naturalPrompt && result.stableDiffusionPrompt) {
    return {
      naturalPrompt: result.naturalPrompt,
      stableDiffusionPrompt: result.stableDiffusionPrompt,
      ...(result.negativePrompt ? { negativePrompt: result.negativePrompt } : {}),
    } as ImagePlanPrompts;
  }

  // If we got here, there is no usable structure
  throw new Error('Free provider returned non-JSON without extractable prompts');
}

/**
 * Free Test Provider
 * 
 * Server-managed evaluation model with limited availability and capabilities.
 * Supports text generation (including image prompt generation) but not actual image generation.
 * All requests are proxied through the backend.
 */
export class FreeTestProvider extends BaseProvider {
  async validateApiKey(
    config: { apiKey: string; model?: string; customEndpoint?: string }
  ): Promise<ValidationResult> {
    // Free provider doesn't require API key validation on client
    return {
      isValid: true,
    };
  }

  async generateCharacterProfile(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    _worldDocument?: WorldDocument,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<CharacterProfile>> {
    return this._makeServerRequest<CharacterProfile>(
      '/pyapi/v1/ai/request',
      {
        capability: 'text',
        messages: [
          { role: 'system', text: prompts.system },
          { role: 'user', text: prompts.user },
        ],
        temperature: config.temperature,
      },
      opts?.signal,
      (content) => parseJsonLike<CharacterProfile>(content)
    );
  }

  async generateChatExportFields(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<ChatExportFields>> {
    return this._makeServerRequest<ChatExportFields>(
      '/pyapi/v1/ai/request',
      {
        capability: 'text',
        messages: [
          { role: 'system', text: prompts.system },
          { role: 'user', text: prompts.user },
        ],
        temperature: config.temperature,
      },
      opts?.signal,
      (content) => parseJsonLike<ChatExportFields>(content)
    );
  }

  async generateImagePlanPrompts(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<ImagePlanPrompts>> {
    return this._makeServerRequest<ImagePlanPrompts>(
      '/pyapi/v1/ai/request',
      {
        capability: 'text',
        messages: [
          { role: 'system', text: prompts.system },
          { role: 'user', text: prompts.user },
        ],
        temperature: config.temperature,
      },
      opts?.signal,
      (content) => parseImagePlanPromptsRobust(content)
    );
  }

  async rewriteCharacterField(
    instructions: string,
    config: APIKeyConfig,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<string>> {
    // For free provider, instructions are passed as user message
    return this._makeServerRequest<string>(
      '/pyapi/v1/ai/request',
      {
        capability: 'text',
        messages: [
          { role: 'system', text: 'Rewrite the following character field as instructed.' },
          { role: 'user', text: instructions },
        ],
        temperature: config.temperature,
      },
      opts?.signal,
      (content) => String(content ?? '').trim()
    );
  }

  async editCharacter(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    opts?: EditOptions
  ): Promise<ProviderResponse<any>> {
    // For free provider, send system and user prompts separately
    return this._makeServerRequest<any>(
      '/pyapi/v1/ai/request',
      {
        capability: 'text',
        messages: [
          { role: 'system', text: prompts.system },
          { role: 'user', text: prompts.user },
        ],
        temperature: config.temperature,
      },
      opts?.signal,
      (content) => parseJsonLike<any>(content)
    );
  }

  async generateRoleplayReply(
    _params: {
      system: string;
      messages: Array<{ role: 'user' | 'assistant'; content: string }>;
    },
    _config: APIKeyConfig,
    _opts?: RoleplayOptions
  ): Promise<ProviderResponse<string>> {
    throw new Error('Free provider does not support roleplay generation');
  }

  /**
   * Make a server request to the unified AI endpoint
   */
  private async _makeServerRequest<T>(
    endpoint: string,
    payload: {
      capability: 'text';
      messages: Array<{ role: string; text: string }>;
      temperature?: number;
    },
    signal?: AbortSignal,
    transform?: (content: string) => T
  ): Promise<ProviderResponse<T>> {
    const auth = getFirebaseAuth();
    const currentUser = auth.currentUser;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (currentUser) {
      Object.assign(headers, await buildAuthHeaders(currentUser));
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Free provider request failed: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    
    // Extract the response content
    let content: string;
    if (typeof data.content === 'string') {
      content = data.content;
    } else if (data.choices?.[0]?.message?.content) {
      content = data.choices[0].message.content;
    } else {
      throw new Error('Invalid response format from free provider');
    }

    // Parse usage if available
    const usage = data.usage ? parseOpenAIUsage(data.usage) : null;

    // Transform or parse the content based on expected type
    let value: T;
    try {
      value = transform ? transform(content) : parseJsonLike<T>(content);
    } catch (err) {
      const message = (err instanceof Error ? err.message : String(err)) || 'Failed to parse provider response';
      throw new Error(message);
    }

    return { value, usage };
  }
}
